import React, { useState, useEffect } from 'react';
import './create_project_dialog.css';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AddProject, selectProject, changeProjectEvent, changeProjectProblem, selectEvent } from '../../../redux/project/projects';
import { createProjectToDB } from '../../../api/project';
import { createEventAPI } from '../../../api/event';
import { CreateProblem } from '../../../api/problem';
import { SortProblemList } from '../../../functions/problem_sorting';
import { showAlert } from '../../../functions/show_alert';
import { HideProgressAlert, showProgressAlert } from '../../../functions/show_progress_page';
import { hundleRequestFailure } from '../../../functions/hundle_request_failure';
import ProjectWizard from './project_wizard/project_wizard';

export default function CreateProjectDialog({ onClose, onWizardStateChange }) {
    const [selectedOption, setSelectedOption] = useState('manual');
    const [showWizard, setShowWizard] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [projectName, setProjectName] = useState('');

    // Notify parent when wizard state changes
    useEffect(() => {
        if (onWizardStateChange) {
            onWizardStateChange(showWizard);
        }
    }, [showWizard, onWizardStateChange]);

    const handleOptionChange = (option) => {
        setSelectedOption(option);
    };

    const handleContinue = async () => {
        if (selectedOption === 'manual') {
            // Option 1: Create project manually (current implementation)
            if (projectName === '') {
                showAlert(false, 'Enter Project Name');
                return;
            }
            showProgressAlert();
            const response = await createProjectToDB(projectName);
            HideProgressAlert();
            if (response.statusCode === 200) {
                const newProject = response.response;
                dispatch(AddProject(newProject));
                dispatch(selectProject(newProject));
                onClose();
                navigate('/event');
            } else {
                hundleRequestFailure(response);
            }
        } else if (selectedOption === 'wizard') {
            // Option 2: Show wizard
            setShowWizard(true);
        }
    };

    const handleWizardClose = () => {
        setShowWizard(false);
        onClose();
    };

    const handleWizardComplete = async (projectData) => {
        // Handle wizard completion - create project first, then event
        showProgressAlert();

        // Create project
        const projectResponse = await createProjectToDB(projectData.name);
        if (projectResponse.statusCode !== 200) {
            HideProgressAlert();
            hundleRequestFailure(projectResponse);
            return;
        }

        const newProject = projectResponse.response;
        dispatch(AddProject(newProject));
        dispatch(selectProject(newProject));

        // Create event if event data exists
        if (projectData.eventData && Object.keys(projectData.eventData).length > 0) {
            const eventData = projectData.eventData;
            const eventResponse = await createEventAPI(
                eventData.eventName,
                eventData.location,
                eventData.eventDesc,
                eventData.negative,
                eventData.problem,
                newProject.id,
                eventData.file || null,
                eventData.eventType,
                eventData.eventDate,
                eventData.youtubeLink || null
            );

            if (eventResponse.statusCode === 200) {
                const createdEvent = eventResponse.response;
                dispatch(changeProjectEvent({ id: newProject.id, event: createdEvent }));
                dispatch(selectEvent({ eventId: createdEvent.id }));

                // Create problem if problem data exists
                if (projectData.problemData &&
                    projectData.problemData.pickedPMT &&
                    projectData.problemData.pickedPMT.length > 0 &&
                    projectData.problemData.pickedPD &&
                    projectData.problemData.pickedPD.length > 0) {

                    const sortedProblemData = SortProblemList(
                        projectData.problemData.pickedPMT,
                        eventData.eventType
                    );

                    const problemResponse = await CreateProblem(
                        newProject.id,
                        projectData.problemData.pickedPD,
                        sortedProblemData,
                        eventData.eventType
                    );

                    HideProgressAlert();

                    if (problemResponse.statusCode < 300) {
                        dispatch(changeProjectProblem({ id: newProject.id, problem: problemResponse.response }));

                        // Store the problem ID in localStorage for this event so it can be found later
                        const problemId = problemResponse.response?.id;
                        if (problemId && createdEvent?.id) {
                            const getStorageKey = () => `eventProblems_${newProject.id || 'default'}`;
                            try {
                                const stored = localStorage.getItem(getStorageKey());
                                const eventProblemMap = stored ? new Map(JSON.parse(stored)) : new Map();
                                eventProblemMap.set(createdEvent.id, problemId);
                                localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(eventProblemMap.entries())));
                            } catch (error) {
                                console.error('Failed to save event problem map:', error);
                            }
                        }

                        showAlert(true, 'Project, Event, and Problem Created Successfully!');
                    } else {
                        hundleRequestFailure(problemResponse);
                        showAlert(true, 'Project and Event Created, but Problem creation failed');
                    }
                } else {
                    HideProgressAlert();
                    showAlert(true, 'Project and Event Created Successfully!');
                }
            } else {
                HideProgressAlert();
                hundleRequestFailure(eventResponse);
                // Still navigate even if event creation fails
            }
        } else {
            HideProgressAlert();
        }

        setShowWizard(false);
        onClose();
        navigate('/event');
    };

    if (showWizard) {
        return <ProjectWizard onClose={handleWizardClose} onComplete={handleWizardComplete} />;
    }

    return (
        <div className="create-project-dialog-overlay" onClick={(e) => {
            if (e.target.className === 'create-project-dialog-overlay') {
                onClose();
            }
        }}>
            <div className="create-project-dialog">
                <div className="dialog-header">
                    <h2 className="dialog-title">Create New Project</h2>
                    <CloseIcon className="close-icon" onClick={onClose} />
                </div>
                <div className="dialog-content">
                    <div className="radio-options">
                        <label className={`radio-option ${selectedOption === 'manual' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="projectCreation"
                                value="manual"
                                checked={selectedOption === 'manual'}
                                onChange={() => handleOptionChange('manual')}
                            />
                            <span className="radio-label">Create project manually</span>
                        </label>
                        <label className={`radio-option ${selectedOption === 'wizard' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="projectCreation"
                                value="wizard"
                                checked={selectedOption === 'wizard'}
                                onChange={() => handleOptionChange('wizard')}
                            />
                            <span className="radio-label">Create project using step wizard</span>
                        </label>
                    </div>
                    {selectedOption === 'manual' && (
                        <div className="manual-input-section">
                            <label className="input-label">Project Name*</label>
                            <input
                                type="text"
                                className="project-name-input"
                                placeholder="Type here"
                                value={projectName}
                                onChange={(e) => setProjectName(e.target.value)}
                                maxLength={50}
                            />
                        </div>
                    )}
                </div>
                <div className="dialog-footer">
                    <button className="footer-button cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="footer-button continue" onClick={handleContinue}>
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}
